import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Truck, LogOut, DollarSign, MapPin, Clock, TrendingUp, Crown, RefreshCw, Navigation2, Car, Wallet } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { driverAPI, membershipAPI } from '../services/api';
import QuoteSubmissionCard from '../components/QuoteSubmissionCard';
import { DriverEarningsModal } from '../components/DriverEarningsModal';
import DriverVerificationCard from '../components/DriverVerificationCard';

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isOnline, setIsOnline] = useState(false);
  const [activeTab, setActiveTab] = useState('requests');
  const [serviceRequests, setServiceRequests] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requiresMembership, setRequiresMembership] = useState(false);
  const [previousRequestCount, setPreviousRequestCount] = useState(0);
  const [activeJobs, setActiveJobs] = useState([]);
  const [previousActiveJobsCount, setPreviousActiveJobsCount] = useState(0);
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [earningsSummary, setEarningsSummary] = useState({});
  const [verificationStatus, setVerificationStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, [isOnline]);

  // Polling for new service requests (every 30 seconds when online)
  useEffect(() => {
    if (!isOnline) return;

    const pollInterval = setInterval(() => {
      fetchNewRequests();
      fetchActiveJobs(); // Also poll for active jobs
    }, 30000); // 30 seconds

    return () => clearInterval(pollInterval);
  }, [isOnline, previousRequestCount, previousActiveJobsCount]);

  // Report current location while online, so customers get a real ETA
  useEffect(() => {
    if (!isOnline || !navigator.geolocation) return;

    const reportLocation = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          driverAPI
            .updateLocation(position.coords.latitude, position.coords.longitude)
            .catch((error) => console.error('Error updating location:', error));
        },
        (error) => console.error('Error getting location:', error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    };

    reportLocation();
    const locationInterval = setInterval(reportLocation, 20000); // 20 seconds

    return () => clearInterval(locationInterval);
  }, [isOnline]);

  const fetchActiveJobs = async () => {
    try {
      const response = await driverAPI.getActiveJobs();
      const jobs = response.active_jobs || [];
      
      // Check if there are new active jobs (customer paid)
      if (jobs.length > previousActiveJobsCount && previousActiveJobsCount >= 0) {
        const newJobsCount = jobs.length - previousActiveJobsCount;
        if (newJobsCount > 0) {
          toast({
            title: '💰 Payment Received!',
            description: `${newJobsCount} customer${newJobsCount > 1 ? 's have' : ' has'} paid. Ready to start service!`,
            duration: 8000,
          });
          
          // Play notification sound
          if (typeof Audio !== 'undefined') {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiDcIF2S57OSlTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBQ==');
            audio.play().catch(e => console.log('Audio play failed:', e));
          }
        }
      }
      
      setPreviousActiveJobsCount(jobs.length);
      setActiveJobs(jobs);
    } catch (error) {
      console.error('Error fetching active jobs:', error);
    }
  };

  const fetchNewRequests = async () => {
    try {
      const requestsData = await driverAPI.getAvailableRequests();
      const newRequests = requestsData.service_requests || [];
      
      // Check if there are new requests
      if (newRequests.length > previousRequestCount && previousRequestCount > 0) {
        const newCount = newRequests.length - previousRequestCount;
        toast({
          title: '🔔 New Service Request!',
          description: `${newCount} new service request${newCount > 1 ? 's' : ''} available for bidding`,
          duration: 5000,
        });
        
        // Play notification sound (optional)
        if (typeof Audio !== 'undefined') {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiDcIF2S57OSlTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2Yg3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBS+BzvLYiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FL4HO8tmINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBS6BzvLaiDcIGGS77OelTxELTqXh8LSCLQUugc7y2og3CBhku+znpU8RC06l4fC0gi0FLoHO8tqINwgYZLvs56VPEQtOpeHwtIItBQ==');
          audio.play().catch(e => console.log('Audio play failed:', e));
        }
      }
      
      setPreviousRequestCount(newRequests.length);
      setServiceRequests(newRequests);
    } catch (error) {
      console.error('Error fetching new requests:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch service requests
      const requestsData = await driverAPI.getAvailableRequests();
      const requests = requestsData.service_requests || [];
      setServiceRequests(requests);
      setPreviousRequestCount(requests.length); // Initialize count
      setRequiresMembership(requestsData.requires_membership || false);

      // Fetch active jobs
      const activeJobsData = await driverAPI.getActiveJobs();
      const jobs = activeJobsData.active_jobs || [];
      setActiveJobs(jobs);
      setPreviousActiveJobsCount(jobs.length);

      // Fetch earnings
      const earningsData = await driverAPI.getEarnings();
      setEarnings(earningsData.earnings || []);
      setTotalEarnings(earningsData.summary?.total_earnings || 0);
      setEarningsSummary(earningsData.summary || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteJob = async (job) => {
    if (!window.confirm(`Mark the ${job.service} job for ${job.customer} as complete? Only do this once the work is finished.`)) return;
    try {
      await driverAPI.completeRequest(job.id);
      toast({ title: 'Job complete', description: 'Your earnings have been added to your balance.' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to mark the job complete',
        variant: 'destructive'
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleToggleOnline = async (checked) => {
    // Check if driver is verified before going online
    if (checked && verificationStatus !== 'approved') {
      toast({
        title: 'Verification Required',
        description: 'You need to submit and be approved for license & insurance verification before going online',
        variant: 'destructive'
      });
      return;
    }

    // Check if driver has active membership before going online
    if (checked && requiresMembership) {
      toast({
        title: 'Membership Required',
        description: 'You need an active membership to go online and receive service requests',
        variant: 'destructive'
      });
      return;
    }

    try {
      await driverAPI.toggleOnline(checked);
      setIsOnline(checked);
      toast({
        title: checked ? 'You are now Online' : 'You are now Offline',
        description: checked ? 'You will receive service requests' : 'You will not receive requests'
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update online status';
      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive'
      });
    }
  };

  const handleQuoteSubmitted = () => {
    // Refresh the service requests list
    fetchData();
    toast({
      title: 'Quote Submitted!',
      description: 'Customer will see your quote'
    });
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
      {/* Header */}
      <div className="border-b" style={{ background: 'linear-gradient(to right, #17283A, #0F1D2B)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/logo.png" 
                alt="Wheely Rescue Logo" 
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-white">Wheely <span className="hidden sm:inline">Driver</span></h1>
                <p className="text-xs text-wheely-orange-light hidden sm:block">Welcome back, {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: requiresMembership ? 'rgba(235, 152, 104, 0.1)' : 'rgba(225, 116, 49, 0.2)' }}>
                <span className="text-white text-sm font-semibold hidden sm:inline">{isOnline ? 'Online' : 'Offline'}</span>
                <Switch
                  checked={isOnline}
                  onCheckedChange={handleToggleOnline}
                  disabled={requiresMembership || verificationStatus !== 'approved'}
                  className="data-[state=checked]:bg-green-500"
                />
                {isOnline && (
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/driver/membership')}
                style={{ borderColor: 'rgba(225, 116, 49, 0.3)', color: '#E17431' }}
                className="hover:bg-wheely-orange/10"
              >
                <Crown className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Membership</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/driver/payouts')}
                style={{ borderColor: 'rgba(16, 185, 129, 0.5)', color: '#10b981' }}
                className="hover:bg-green-500/10"
              >
                <Wallet className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Payouts</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                style={{ borderColor: 'rgba(235, 152, 104, 0.5)', color: '#EB9868' }}
                className="hover:bg-wheely-orange/10"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DriverVerificationCard onStatusChange={setVerificationStatus} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card 
            onClick={() => setShowEarningsModal(true)}
            className="border-0 cursor-pointer hover:scale-105 transition-transform duration-200" 
            style={{ background: 'linear-gradient(to bottom right, #10b981, #059669)' }}
          >
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Total Earnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-white">£{totalEarnings.toFixed(2)}</p>
              <p className="text-green-100 text-sm mt-1">From {earnings.length} completed jobs</p>
              <p className="text-green-200 text-xs mt-2 font-semibold">Click to view details</p>
            </CardContent>
          </Card>

          <Card className="border-0" style={{ background: 'linear-gradient(to bottom right, #E17431, #EB9868)' }}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Completed Jobs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-white">{earnings.length}</p>
              <p className="text-orange-100 text-sm mt-1">This month</p>
            </CardContent>
          </Card>

          <Card className="border-0" style={{ background: 'linear-gradient(to bottom right, #E17431, #2EB2D5)' }}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Pending Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-white">{serviceRequests.length}</p>
              <p className="text-cyan-100 text-sm mt-1">Available now</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('requests')}
            style={{
              background: activeTab === 'requests' ? 'linear-gradient(to right, #E17431, #EB9868)' : 'rgba(30, 58, 74, 0.8)',
              color: activeTab === 'requests' ? 'white' : '#94a3b8'
            }}
            className="hover:opacity-90"
          >
            Service Requests {serviceRequests.length > 0 && `(${serviceRequests.length})`}
          </Button>
          <Button
            onClick={() => setActiveTab('active')}
            style={{
              background: activeTab === 'active' ? 'linear-gradient(to right, #10b981, #059669)' : 'rgba(30, 58, 74, 0.8)',
              color: activeTab === 'active' ? 'white' : '#94a3b8'
            }}
            className="hover:opacity-90"
          >
            Active Jobs {activeJobs.length > 0 && `(${activeJobs.length})`}
          </Button>
          <Button
            onClick={() => setActiveTab('earnings')}
            style={{
              background: activeTab === 'earnings' ? 'linear-gradient(to right, #E17431, #EB9868)' : 'rgba(30, 58, 74, 0.8)',
              color: activeTab === 'earnings' ? 'white' : '#94a3b8'
            }}
            className="hover:opacity-90"
          >
            Earnings History
          </Button>
          <Button
            onClick={fetchData}
            disabled={loading}
            style={{ background: 'rgba(225, 116, 49, 0.3)', color: '#E17431' }}
            className="ml-auto hover:bg-wheely-orange/20/30"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>

        {/* Service Requests */}
        {activeTab === 'requests' && (
          <div className="space-y-4">
            {!isOnline && (
              <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
                <CardContent className="py-8 text-center">
                  <p className="text-gray-300 text-lg mb-4">You are currently offline</p>
                  <p className="text-gray-400">Toggle the switch above to start receiving requests</p>
                </CardContent>
              </Card>
            )}

            {isOnline && requiresMembership && (
              <Card style={{ background: 'rgba(235, 152, 104, 0.2)', borderColor: 'rgba(235, 152, 104, 0.4)' }}>
                <CardContent className="py-8 text-center">
                  <p className="text-orange-300 text-lg mb-4">Active Membership Required</p>
                  <p className="text-orange-200 mb-4">You need an active membership to receive service requests</p>
                  <Button
                    onClick={() => navigate('/driver/membership')}
                    style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}
                    className="text-white"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    View Membership Plans
                  </Button>
                </CardContent>
              </Card>
            )}

            {isOnline && !requiresMembership && loading && (
              <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
                <CardContent className="py-12 text-center">
                  <RefreshCw className="w-8 h-8 text-wheely-orange-light animate-spin mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">Loading service requests...</p>
                </CardContent>
              </Card>
            )}

            {isOnline && !requiresMembership && !loading && serviceRequests.map((request) => (
              <QuoteSubmissionCard key={request.id} request={request} onQuoteSubmitted={handleQuoteSubmitted} />
            ))}

            {isOnline && !requiresMembership && !loading && serviceRequests.length === 0 && (
              <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-300 text-lg">No pending requests</p>
                  <p className="text-gray-400 text-sm mt-2">New requests will appear here</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Active Jobs (Paid & Ready to Start) */}
        {activeTab === 'active' && (
          <div className="space-y-4">
            {activeJobs.length === 0 ? (
              <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-300 text-lg">No active jobs</p>
                  <p className="text-gray-400 text-sm mt-2">Jobs appear here after customers pay</p>
                </CardContent>
              </Card>
            ) : (
              activeJobs.map((job) => (
                <Card key={job.id} style={{ background: 'rgba(16, 185, 129, 0.1)', borderColor: 'rgba(16, 185, 129, 0.3)' }}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }}>
                            💰 PAID - Ready to Start
                          </Badge>
                        </div>
                        <CardTitle className="text-white text-xl mb-2">{job.service}</CardTitle>
                        <CardDescription className="text-gray-300">{job.customer}</CardDescription>
                        {job.customer_phone !== 'N/A' && (
                          <p className="text-wheely-orange-light text-sm mt-1">📞 {job.customer_phone}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold text-2xl">£{job.price.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-start gap-2 text-gray-300">
                      <MapPin className="w-4 h-4 mt-1 text-wheely-orange-light" />
                      <div>
                        <p className="text-xs text-gray-400">Pickup:</p>
                        <p className="text-white">{job.location}</p>
                      </div>
                    </div>
                    {job.destination && (
                      <div className="flex items-start gap-2 text-gray-300">
                        <Navigation2 className="w-4 h-4 mt-1 text-green-400" />
                        <div>
                          <p className="text-xs text-gray-400">Drop-off:</p>
                          <p className="text-white">{job.destination}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-300">
                      <Car className="w-4 h-4 text-wheely-orange-light" />
                      <span>{job.vehicleModel} ({job.vehicleYear})</span>
                    </div>
                    {job.vehicleCondition && (
                      <div style={{ background: 'rgba(15, 23, 42, 0.8)' }} className="p-3 rounded-lg">
                        <p className="text-xs text-gray-400">Vehicle Condition:</p>
                        <p className="text-white font-semibold">
                          {job.vehicleCondition === 'starts_and_runs' && 'Starts & Runs'}
                          {job.vehicleCondition === 'rolls_neutral' && 'Rolls in Neutral'}
                          {job.vehicleCondition === 'keyless_stuck' && 'Keyless/Stuck (Winch Required)'}
                        </p>
                      </div>
                    )}
                    <div style={{ background: 'rgba(15, 23, 42, 0.8)' }} className="p-3 rounded-lg">
                      <p className="text-gray-400 text-sm">Issue:</p>
                      <p className="text-white">{job.issue}</p>
                    </div>
                    {job.cancellation_requested ? (
                      <div className="p-3 rounded-lg border border-red-500 bg-red-500/15 text-white text-sm">
                        The customer has cancelled. Don't continue to this job - Wheely will settle what you're owed for work done so far.
                      </div>
                    ) : (
                      <Button
                        className="w-full"
                        style={{ background: 'linear-gradient(to right, #10b981, #059669)' }}
                        onClick={() => handleCompleteJob(job)}
                      >
                        Mark complete
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Earnings History */}
        {activeTab === 'earnings' && (
          <div className="space-y-4">
            {earnings.length === 0 ? (
              <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-300 text-lg">No earnings yet</p>
                  <p className="text-gray-400 text-sm mt-2">Complete jobs to start earning</p>
                </CardContent>
              </Card>
            ) : (
              earnings.map((earning) => (
                <Card key={earning.id} style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white text-xl mb-2">{earning.service}</CardTitle>
                        <CardDescription className="text-gray-300">{earning.customer}</CardDescription>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold text-2xl">£{earning.earnings.toFixed(2)}</p>
                        <Badge
                          style={{
                            background: earning.status === 'paid' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(235, 152, 104, 0.2)',
                            color: earning.status === 'paid' ? '#10b981' : '#EB9868'
                          }}
                          className="mt-2"
                        >
                          {earning.status}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span>{new Date(earning.date).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Earnings Modal */}
      <DriverEarningsModal
        isOpen={showEarningsModal}
        onClose={() => setShowEarningsModal(false)}
        earnings={earnings}
        summary={earningsSummary}
      />
    </div>
  );
};

export default DriverDashboard;